import { countryCodeToDialCode } from './SchedulingModal.constants';

export function getDialCodeFromCountryCode(
  countryCode: string | null | undefined,
): string {
  if (!countryCode) {
    return '';
  }
  const upperCode = countryCode.toUpperCase();
  return countryCodeToDialCode[upperCode] || '';
}

export function formatPhoneNumberWithCountryCode(
  phoneNumber: string,
  countryCode?: string | null,
): string {
  // Get country code from parameter or default to MX
  const code = countryCode || 'MX';
  const dialCode = getDialCodeFromCountryCode(code);

  // Remove any leading + or spaces from phone number
  let formattedPhone = phoneNumber.replace(/^\+|\s/g, '');

  // Check if phone number already starts with any known dial code
  const startsWithDialCode = Object.values(countryCodeToDialCode).some((code) =>
    formattedPhone.startsWith(code),
  );

  // Prepend dial code if it doesn't already start with a dial code
  if (dialCode && !startsWithDialCode) {
    formattedPhone = `${dialCode}${formattedPhone}`;
  }

  return formattedPhone;
}

export type tRetentionBookingContact = {
  /** The full name, as the profile composes it from the two parts below. */
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  /** The workspace phone, national or already with its dial code. */
  workspacePhone?: string | null;
  workspaceId?: string | null;
};

export type tBuildRetentionBookingPageUrlOptions = {
  /** A configured booking link, as `retentionBookingUrl` returns it. */
  bookingUrl: string;
  contact: tRetentionBookingContact;
  /** The month a Calendly scheduler opens on. */
  now: Date;
};

/**
 * A HubSpot meetings page lives on `meetings.hubspot.com` (or a regional host
 * such as `meetings-eu1.hubspot.com`), or under `/meetings/` on a domain
 * connected to HubSpot.
 */
const HUBSPOT_MEETINGS_HOST = /^meetings(-[a-z0-9]+)?\.hubspot\.com$/i;

function isHubSpotMeetingsPage(url: URL): boolean {
  return (
    HUBSPOT_MEETINGS_HOST.test(url.hostname) ||
    url.pathname.startsWith('/meetings/')
  );
}

/**
 * First and last name apart, for a form that asks for them apart. The profile
 * already keeps them apart; a session without them falls back to splitting the
 * full name at its first space, and a single word stays a first name — a last
 * name is never made up.
 */
function splitContactName(contact: tRetentionBookingContact): {
  firstName: string | null;
  lastName: string | null;
} {
  const firstName = contact.firstName?.trim();
  if (firstName) {
    return { firstName, lastName: contact.lastName?.trim() || null };
  }

  const [first, ...rest] = (contact.name ?? '').trim().split(/\s+/);
  return {
    firstName: first || null,
    lastName: rest.length > 0 ? rest.join(' ') : null,
  };
}

/**
 * The booking page the retention scheduler embeds, with what we already know
 * about the customer filled in through the provider's own parameters.
 *
 * - A HubSpot meetings page gets `embed=true`, which drops the page's own
 *   chrome inside the iframe, and the contact form's `firstname`, `lastname`
 *   and `email`. HubSpot has no standard field for the workspace phone or id,
 *   so neither is sent. Parameters already on the link, such as the page's
 *   `uuid`, are kept.
 * - Any other page is Calendly, prefilled exactly as before: `month`, `name`,
 *   `email` and the custom answers `a1` (the workspace phone with its dial
 *   code) and `a2` (the workspace id).
 *
 * Null when the link does not parse, so the modal never frames something that
 * is not a URL.
 */
export function buildRetentionBookingPageUrl(
  options: tBuildRetentionBookingPageUrlOptions,
): string | null {
  const { bookingUrl, contact, now } = options;
  let url: URL;
  try {
    url = new URL(bookingUrl);
  } catch {
    return null;
  }

  if (isHubSpotMeetingsPage(url)) {
    const { firstName, lastName } = splitContactName(contact);
    url.searchParams.set('embed', 'true');
    if (firstName) url.searchParams.set('firstname', firstName);
    if (lastName) url.searchParams.set('lastname', lastName);
    if (contact.email) url.searchParams.set('email', contact.email);
    return url.toString();
  }

  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  url.searchParams.set('month', month);
  if (contact.name) url.searchParams.set('name', contact.name);
  if (contact.email) url.searchParams.set('email', contact.email);
  if (contact.workspacePhone) {
    url.searchParams.set(
      'a1',
      formatPhoneNumberWithCountryCode(contact.workspacePhone),
    );
  }
  if (contact.workspaceId) url.searchParams.set('a2', contact.workspaceId);
  return url.toString();
}
